import React from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  /** Input label */
  label?: string;
  /** Required field indicator */
  required?: boolean;
  /** Icon to display inside the input */
  icon?: React.ReactNode;
  /** Error message */
  error?: string;
  /** Helper text */
  helperText?: string;
  /** Multiline textarea */
  multiline?: boolean;
  /** Number of rows for textarea */
  rows?: number;
}

/**
 * Input component from AgroBook design system
 *
 * Supports text inputs and textareas with optional icons, labels, and validation
 */
export const Input: React.FC<InputProps> = ({
  label,
  required,
  icon,
  error,
  helperText,
  multiline = false,
  rows = 4,
  className = '',
  ...props
}) => {
  const baseInputStyles = 'w-full px-3.5 py-2.75 border-thick rounded-sm font-body text-base bg-white text-gray-900 outline-none transition-agrobook duration-agrobook placeholder:text-gray-400';
  const focusStyles = 'focus:border-green-600 focus:ring-4 focus:ring-green-600/15';
  const errorStyles = error ? 'border-red-400 focus:border-red-500 focus:ring-red-500/15' : 'border-gray-300';
  const iconPadding = icon ? 'ps-10' : '';

  const InputElement = multiline ? 'textarea' : 'input';

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-3xs font-semibold text-gray-700 tracking-wider">
          {label}
          {required && <span className="text-red-600 ms-0.5">*</span>}
        </label>
      )}
      <div className="relative">
        {icon && (
          <span className="absolute start-3.5 top-1/2 -translate-y-1/2 text-gray-500 text-base pointer-events-none">
            {icon}
          </span>
        )}
        <InputElement
          className={`${baseInputStyles} ${focusStyles} ${errorStyles} ${iconPadding} ${multiline ? 'resize-none' : ''} ${className}`}
          rows={multiline ? rows : undefined}
          {...props}
        />
      </div>
      {(error || helperText) && (
        <p className={`text-xs ${error ? 'text-red-600' : 'text-gray-500'}`}>
          {error || helperText}
        </p>
      )}
    </div>
  );
};
